import { useSearchParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import type { TemplateSettings } from "@/profitability/settings/types";
import { formatReportPeriod } from "@/profitability/utils";

export const useRulePeriod = (
  periods: NonNullable<TemplateSettings["periods"]>,
) => {
  const [params, setParams] = useSearchParams();
  const runId = params.get("period") ?? periods.at(-1)?.run_id ?? null;
  const select = (id: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("period", id);
      return next;
    });
  return { runId, select };
};

export const RulePeriodSelect = ({
  periods,
  runId,
  onChange,
}: {
  periods: NonNullable<TemplateSettings["periods"]>;
  runId: string | null;
  onChange: (id: string) => void;
}) => {
  if (!runId) return null;
  const items = [...periods].reverse().map((period) => ({
    value: period.run_id,
    label: formatReportPeriod(period),
  }));
  if (!items.some((item) => item.value === runId))
    items.unshift({ value: runId, label: "Selected period" });
  return (
    <Select
      value={runId}
      items={Object.fromEntries(items.map((item) => [item.value, item.label]))}
      onValueChange={(value) => value && onChange(value)}
    >
      <SelectTrigger aria-label="Rule period" className="w-full sm:w-auto">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="pointer-coarse:[&_[data-slot=select-item]]:min-h-11">
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
